// Apolo IDE — UpdateNotification (Sprint v8 C7.4).
//
// Bottom-right toast that surfaces electron-updater events. Listens via
// the preload bridge (window.apolo.updater.on) — the main process owns
// the autoUpdater (electron/autoUpdater.cjs).
//
// Flow:
//   1. updater fires `update-available` → toast: "Atualização vX.Y.Z disponível"
//   2. User clicks "Baixar"             → calls window.apolo.updater.download()
//   3. `progress` events update the bar.
//   4. `downloaded` fires               → toast: "Pronto pra instalar"
//   5. User clicks "Reiniciar e atualizar" → calls window.apolo.updater.install()
//
// In a browser context (no window.apolo), this component is inert.

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, RefreshCcw, X, AlertCircle, CheckCircle2 } from 'lucide-react';

const SPRING = { type: 'spring', stiffness: 320, damping: 28 };

export default function UpdateNotification() {
  const [state, setState] = useState({ kind: 'idle', info: null });

  useEffect(() => {
    const updater = typeof window !== 'undefined' ? window.apolo?.updater : null;
    if (!updater?.on) return undefined;
    const offAvail = updater.on('update-available', (info) =>
      setState({ kind: 'available', info }),
    );
    const offProgress = updater.on('progress', (info) =>
      setState((prev) =>
        prev.kind === 'downloading' || prev.kind === 'available'
          ? { kind: 'downloading', info: { ...prev.info, percent: info?.percent } }
          : prev,
      ),
    );
    const offDone = updater.on('downloaded', (info) =>
      setState({ kind: 'downloaded', info }),
    );
    const offErr = updater.on('error', (info) =>
      setState({ kind: 'error', info }),
    );
    return () => {
      offAvail?.();
      offProgress?.();
      offDone?.();
      offErr?.();
    };
  }, []);

  function dismiss() {
    setState({ kind: 'idle', info: null });
  }

  async function download() {
    setState((prev) => ({ kind: 'downloading', info: { ...prev.info, percent: 0 } }));
    await window.apolo?.updater?.download?.();
  }

  async function install() {
    await window.apolo?.updater?.install?.();
  }

  const visible = state.kind !== 'idle';

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          role="status"
          aria-live="polite"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={SPRING}
          className="fixed bottom-4 right-4 z-50 w-[min(22rem,calc(100vw-2rem))] rounded-xl border border-apolo-borderSubtle bg-apolo-surface1 p-4 shadow-2xl ring-1 ring-apolo-accentRing"
          style={{ boxShadow: 'var(--apolo-glow-accent)' }}
        >
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-apolo-accentMuted text-apolo-accent">
              {state.kind === 'available' && <Download size={16} aria-hidden="true" />}
              {state.kind === 'downloading' && <Download size={16} aria-hidden="true" />}
              {state.kind === 'downloaded' && <CheckCircle2 size={16} aria-hidden="true" />}
              {state.kind === 'error' && <AlertCircle size={16} aria-hidden="true" className="text-apolo-error" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-apolo-textPrimary">
                {state.kind === 'available' && 'Atualização disponível'}
                {state.kind === 'downloading' && 'Baixando…'}
                {state.kind === 'downloaded' && 'Pronto pra atualizar'}
                {state.kind === 'error' && 'Falha ao verificar'}
              </p>
              <p className="mt-0.5 text-xs text-apolo-textSecondary">
                {state.kind === 'available' && (
                  <>
                    Versão <span className="font-medium text-apolo-textPrimary">v{state.info?.version || 'nova'}</span>
                    {' '}já está pronta. Quer baixar agora?
                  </>
                )}
                {state.kind === 'downloading' && (
                  <>
                    {Math.round(state.info?.percent ?? 0)}% — você pode continuar usando.
                  </>
                )}
                {state.kind === 'downloaded' && (
                  <>
                    v<span className="font-medium text-apolo-textPrimary">{state.info?.version}</span> baixada.
                  </>
                )}
                {state.kind === 'error' && (
                  <span className="line-clamp-2">
                    {state.info?.message || 'Tente novamente em alguns minutos.'}
                  </span>
                )}
              </p>
              {state.kind === 'downloading' && (
                <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-apolo-surface3">
                  <motion.div
                    className="h-full bg-apolo-accent"
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.round(state.info?.percent ?? 0)}%` }}
                    transition={{ duration: 0.25 }}
                  />
                </div>
              )}
              {state.kind === 'available' && (
                <div className="mt-3 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={download}
                    className="inline-flex items-center gap-1.5 rounded-md bg-apolo-accent px-3 py-1.5 text-xs font-medium text-apolo-bg transition hover:bg-apolo-accentHover focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
                  >
                    <Download size={11} aria-hidden="true" />
                    Baixar
                  </button>
                  <button
                    type="button"
                    onClick={dismiss}
                    className="rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-3 py-1.5 text-xs text-apolo-textSecondary transition hover:border-apolo-borderStrong hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
                  >
                    Depois
                  </button>
                </div>
              )}
              {state.kind === 'downloaded' && (
                <div className="mt-3 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={install}
                    className="inline-flex items-center gap-1.5 rounded-md bg-apolo-accent px-3 py-1.5 text-xs font-medium text-apolo-bg transition hover:bg-apolo-accentHover focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
                  >
                    <RefreshCcw size={11} aria-hidden="true" />
                    Reiniciar e atualizar
                  </button>
                  <button
                    type="button"
                    onClick={dismiss}
                    className="rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-3 py-1.5 text-xs text-apolo-textSecondary transition hover:border-apolo-borderStrong hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
                  >
                    Depois
                  </button>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dispensar notificação"
              className="shrink-0 rounded-md p-1 text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
            >
              <X size={12} aria-hidden="true" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
